import type { CreatePulsarFileRequest, PulsarFileAccess, PulsarFileRequest } from '../types/pulsarFiles';
import { PulsarApiError } from './pulsarApi';

export const PULSAR_FILES_BASE = 'https://spy.playcdu.co/api/notes/pulsar-files';

async function request<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${PULSAR_FILES_BASE}${path}`, {
    method: body === undefined ? 'GET' : 'POST', cache: 'no-store', credentials: 'include', signal,
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new PulsarApiError(typeof data?.error === 'string' ? data.error : `Files could not be retrieved (${response.status}).`, response.status);
  return data as T;
}

export const pulsarFilesApi = {
  access: (address: string, signal?: AbortSignal) => request<PulsarFileAccess>(`/servers/${encodeURIComponent(address)}`, undefined, signal),
  create: (body: CreatePulsarFileRequest, signal?: AbortSignal) => request<{ request: PulsarFileRequest }>('/requests', body, signal),
  status: (id: string, signal?: AbortSignal) => request<{ request: PulsarFileRequest }>(`/requests/${encodeURIComponent(id)}`, undefined, signal),
};

/** Downloads stay behind staff authentication and never load an entire file into JS memory. */
export function pulsarDownloadUrl(request: PulsarFileRequest): string | null {
  const expected = `${PULSAR_FILES_BASE}/requests/${encodeURIComponent(request.id)}/download`;
  return request.state === 'succeeded' && request.download_url === expected ? expected : null;
}

/** AMP performs its own login and permission checks; no credentials are placed in the link. */
export function pulsarAmpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.origin === 'https://amp.playcdu.co' && !url.username && !url.password && !url.search && !url.hash
      && /^\/instances\/[a-f\d]{8}\/?$/i.test(url.pathname) ? url.href : null;
  } catch { return null; }
}
